'use client';

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { useAnalytics } from '@/hooks/useAnalytics';

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

interface FormState {
  name: string;
  email: string;
  subject: string;
  message: string;
}

export default function ContactPage() {
  const { trackFeature } = useAnalytics();
  const [formState, setFormState] = useState<FormState>({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState<Partial<FormState>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    const errors: Partial<FormState> = {};
    
    if (!formState.name.trim()) errors.name = 'Name is required';
    if (!formState.email.trim()) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formState.email)) errors.email = 'Email is invalid';
    if (!formState.subject.trim()) errors.subject = 'Subject is required';
    if (!formState.message.trim()) errors.message = 'Message is required';
    
    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setIsSuccess(false);

    if (!validateForm()) {
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formState),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      trackFeature('contact', 'form_submitted');
      setIsSuccess(true);
    } catch (error) {
      console.error('Error sending message:', error);
      // You might want to show an error message to the user here
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-futuristic-black">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-futuristic-blue/20 via-futuristic-purple/10 to-transparent opacity-80" />
          
          {/* Animated background elements */}
          <div className="absolute top-20 right-20 w-72 h-72 bg-futuristic-blue/10 rounded-full filter blur-3xl animate-float"></div>
          <div className="absolute bottom-10 left-10 w-64 h-64 bg-futuristic-purple/10 rounded-full filter blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-futuristic-blue via-futuristic-purple to-futuristic-neon bg-clip-text text-transparent">
                Get in Touch
              </span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Have questions about Sketchplay? We're here to help. Reach out to us through the form below or connect with us on social media.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 relative">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-futuristic-blue/50 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-futuristic-purple/50 to-transparent"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Form */}
            <motion.div 
              initial="hidden"
              animate="visible"
              variants={fadeIn}
              className="lg:col-span-2"
            >
              {isSuccess ? (
                <div className="bg-futuristic-dark/40 backdrop-blur-md rounded-2xl border border-white/10 p-8 md:p-10 shadow-xl text-center">
                  <div className="w-20 h-20 rounded-full bg-futuristic-neon/20 flex items-center justify-center mx-auto mb-6">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-futuristic-neon" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h2 className="text-3xl font-bold text-white mb-4">Message Sent!</h2>
                  <p className="text-xl text-gray-300 mb-8">
                    Thank you for reaching out. We'll get back to you as soon as possible.
                  </p>
                  <div className="space-y-4">
                    <p className="text-sm text-gray-400">
                      A confirmation email has been sent to {formState.email}
                    </p>
                    <Button
                      variant="futuristic"
                      size="lg"
                      onClick={() => {
                        setFormState({ name: '', email: '', subject: '', message: '' });
                        setIsSuccess(false);
                      }}
                      trackingName="send_another_message"
                      trackingLocation="contact_success"
                    >
                      Send Another Message
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="bg-futuristic-dark/40 backdrop-blur-md rounded-2xl border border-white/10 p-8 md:p-10 shadow-xl">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Name */}
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                          Your Name <span className="text-futuristic-neon">*</span>
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formState.name}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 bg-futuristic-black/60 border ${errors.name ? 'border-red-500' : 'border-white/10'} rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-futuristic-blue/50 focus:border-transparent transition-colors`}
                          placeholder="Your full name"
                        />
                        {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
                      </div>
                      
                      {/* Email */}
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                          Email <span className="text-futuristic-neon">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formState.email}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 bg-futuristic-black/60 border ${errors.email ? 'border-red-500' : 'border-white/10'} rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-futuristic-blue/50 focus:border-transparent transition-colors`}
                          placeholder="your.email@example.com"
                        />
                        {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
                      </div>
                    </div>
                    
                    {/* Subject */}
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-300 mb-2">
                        Subject <span className="text-futuristic-neon">*</span>
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formState.subject}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 bg-futuristic-black/60 border ${errors.subject ? 'border-red-500' : 'border-white/10'} rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-futuristic-blue/50 focus:border-transparent transition-colors`}
                        placeholder="What's this about?"
                      />
                      {errors.subject && <p className="mt-1 text-sm text-red-500">{errors.subject}</p>}
                    </div>
                    
                    {/* Message */}
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                        Message <span className="text-futuristic-neon">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formState.message}
                        onChange={handleInputChange}
                        rows={6}
                        className={`w-full px-4 py-3 bg-futuristic-black/60 border ${errors.message ? 'border-red-500' : 'border-white/10'} rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-futuristic-blue/50 focus:border-transparent transition-colors`}
                        placeholder="Your message..."
                      />
                      {errors.message && <p className="mt-1 text-sm text-red-500">{errors.message}</p>}
                    </div>
                    
                    <Button
                      type="submit"
                      variant="athletic"
                      size="lg"
                      isLoading={isSubmitting}
                      trackingName="submit_contact_form"
                      trackingLocation="contact_page"
                      className="w-full md:w-auto"
                    >
                      Send Message
                    </Button>
                  </form>
                </div>
              )}
            </motion.div>
            
            {/* Contact Information */}
            <motion.div 
              initial="hidden"
              animate="visible"
              variants={fadeIn}
              className="lg:col-span-1"
            >
              <div className="bg-futuristic-dark/40 backdrop-blur-md rounded-2xl border border-white/10 p-8 md:p-10 shadow-xl h-full">
                <h2 className="text-2xl font-bold text-white mb-8">Contact Information</h2>
                
                <div className="space-y-8">
                  {/* Email */}
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-futuristic-blue/20 flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-futuristic-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-white">Email</h3>
                      <p className="mt-1 text-gray-300">
                        <span>info</span>
                        <span>&#64;</span>
                        <span>sketchplay</span>
                        <span>&#46;</span>
                        <span>com</span>
                      </p>
                    </div>
                  </div>
                  
                  {/* Phone */}
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-futuristic-purple/20 flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-futuristic-purple" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-white">Phone</h3>
                      <p className="mt-1 text-gray-300">+1 (904)-404-9968</p>
                    </div>
                  </div>
                  
                  {/* Location */}
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-futuristic-neon/20 flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-futuristic-neon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-white">Location</h3>
                      <p className="mt-1 text-gray-300">Jacksonville, FL</p>
                    </div>
                  </div>
                </div>
                
                {/* Social Links */}
                <div className="mt-12 pt-8 border-t border-white/10">
                  <h3 className="text-lg font-medium text-white mb-4">Follow Us</h3>
                  <div className="flex space-x-4">
                    <a href="https://x.com/sketchplayapp" target="_blank" className="text-gray-400 hover:text-futuristic-blue transition-colors">
                      <span className="sr-only">Twitter</span>
                      <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                      </svg>
                    </a>
                    <a href="https://linkedin.com/company/sketchplay" target="_blank" className="text-gray-400 hover:text-futuristic-blue transition-colors">
                      <span className="sr-only">LinkedIn</span>
                      <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-.88-.06-1.601-1-1.601-1 0-1.16.781-1.16 1.601v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                      </svg>
                    </a>
                    <a href="https://www.tiktok.com/@sketchplayapp" target="_blank" className="text-gray-400 hover:text-futuristic-blue transition-colors">
                      <span className="sr-only">TikTok</span>
                      <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
                      </svg>
                    </a>
                    <a href="https://instagram.com/sketchplayapp" target="_blank" className="text-gray-400 hover:text-futuristic-blue transition-colors">
                      <span className="sr-only">Instagram</span>
                      <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                        <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.09 1.064.077 1.791.232 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.233.636.388 1.363.465 2.427.077 1.067.09 1.407.09 4.123v.08c0 2.643-.012 2.987-.09 4.043-.077 1.064-.232 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.233-1.363.388-2.427.465-1.067.077-1.407.09-4.123.09h-.08c-2.643 0-2.987-.012-4.043-.09-1.064-.077-1.791-.232-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.233-.636-.388-1.363-.465-2.427-.047-1.024-.09-1.379-.09-3.808v-.63c0-2.43.013-2.784.09-3.808.077-1.064.232-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.233 1.363-.388 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section className="py-16 relative">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-futuristic-blue/50 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-futuristic-purple/50 to-transparent"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
            className="text-center max-w-4xl mx-auto mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              <span className="bg-gradient-to-r from-futuristic-blue via-futuristic-purple to-futuristic-neon bg-clip-text text-transparent">
                Technical Support
              </span>
            </h2>
            <p className="text-xl text-gray-300">
              Get help with technical issues, account management, and platform features.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Support Topics */}
            <motion.div 
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeIn}
              className="lg:col-span-2"
            >
              <div className="bg-futuristic-dark/40 backdrop-blur-md rounded-2xl border border-white/10 p-8 md:p-10 shadow-xl">
                <h3 className="text-2xl font-bold text-white mb-6">Common Support Topics</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Account & Billing */}
                  <div className="group">
                    <div className="flex items-center space-x-4 p-4 rounded-lg bg-futuristic-black/40 border border-white/5 hover:border-futuristic-blue/30 transition-colors cursor-pointer">
                      <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-futuristic-blue/20 flex items-center justify-center group-hover:bg-futuristic-blue/30 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-futuristic-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-lg font-medium text-white group-hover:text-futuristic-blue transition-colors">Account & Billing</h4>
                        <p className="text-sm text-gray-400">Manage your account settings and billing information</p>
                      </div>
                    </div>
                  </div>

                  {/* Platform Features */}
                  <div className="group">
                    <div className="flex items-center space-x-4 p-4 rounded-lg bg-futuristic-black/40 border border-white/5 hover:border-futuristic-purple/30 transition-colors cursor-pointer">
                      <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-futuristic-purple/20 flex items-center justify-center group-hover:bg-futuristic-purple/30 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-futuristic-purple" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-lg font-medium text-white group-hover:text-futuristic-purple transition-colors">Platform Features</h4>
                        <p className="text-sm text-gray-400">Learn about and troubleshoot platform features</p>
                      </div>
                    </div>
                  </div>

                  {/* API & Integration */}
                  <div className="group">
                    <div className="flex items-center space-x-4 p-4 rounded-lg bg-futuristic-black/40 border border-white/5 hover:border-futuristic-neon/30 transition-colors cursor-pointer">
                      <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-futuristic-neon/20 flex items-center justify-center group-hover:bg-futuristic-neon/30 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-futuristic-neon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-lg font-medium text-white group-hover:text-futuristic-neon transition-colors">API & Integration</h4>
                        <p className="text-sm text-gray-400">Technical support for API and integration issues</p>
                      </div>
                    </div>
                  </div>

                  {/* Mobile App */}
                  <div className="group">
                    <div className="flex items-center space-x-4 p-4 rounded-lg bg-futuristic-black/40 border border-white/5 hover:border-futuristic-blue/30 transition-colors cursor-pointer">
                      <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-futuristic-blue/20 flex items-center justify-center group-hover:bg-futuristic-blue/30 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-futuristic-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-lg font-medium text-white group-hover:text-futuristic-blue transition-colors">Mobile App</h4>
                        <p className="text-sm text-gray-400">Support for mobile app issues and features</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Support Ticket */}
            <motion.div 
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeIn}
              className="lg:col-span-1"
            >
              <div className="bg-futuristic-dark/40 backdrop-blur-md rounded-2xl border border-white/10 p-8 md:p-10 shadow-xl h-full">
                <h3 className="text-2xl font-bold text-white mb-6">Submit Support Ticket</h3>
                <p className="text-gray-300 mb-6">
                  Need technical assistance? Submit a support ticket and our team will help you resolve the issue.
                </p>
                <Button
                  variant="futuristic"
                  size="lg"
                  href="mailto:support@sketchplay.com?subject=Support Ticket Request"
                  trackingName="create_support_ticket"
                  trackingLocation="contact"
                >
                  Create Support Ticket
                </Button>
              </div>
            </motion.div>
          </div>

          {/* Knowledge Base Preview */}
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
            className="mt-16"
          >
            <div className="bg-futuristic-dark/40 backdrop-blur-md rounded-2xl border border-white/10 p-8 md:p-10 shadow-xl">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-white">Knowledge Base</h3>
                <Button
                  variant="futuristic"
                  size="lg"
                  href="/coming-soon"
                  className="w-full justify-center"
                  trackingName="view_all_articles"
                  trackingLocation="contact_page"
                >
                  View All Articles
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Featured Articles */}
                <div className="group">
                  <div className="p-6 rounded-lg bg-futuristic-black/40 border border-white/5 hover:border-futuristic-blue/30 transition-colors cursor-pointer">
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-futuristic-blue/20 flex items-center justify-center group-hover:bg-futuristic-blue/30 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-futuristic-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                      </div>
                      <h4 className="text-lg font-medium text-white group-hover:text-futuristic-blue transition-colors">Getting Started Guide</h4>
                    </div>
                    <p className="text-sm text-gray-400">Learn the basics of using Sketchplay's platform</p>
                  </div>
                </div>

                <div className="group">
                  <div className="p-6 rounded-lg bg-futuristic-black/40 border border-white/5 hover:border-futuristic-purple/30 transition-colors cursor-pointer">
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-futuristic-purple/20 flex items-center justify-center group-hover:bg-futuristic-purple/30 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-futuristic-purple" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                      </div>
                      <h4 className="text-lg font-medium text-white group-hover:text-futuristic-purple transition-colors">Security Best Practices</h4>
                    </div>
                    <p className="text-sm text-gray-400">Keep your account and data secure</p>
                  </div>
                </div>

                <div className="group">
                  <div className="p-6 rounded-lg bg-futuristic-black/40 border border-white/5 hover:border-futuristic-neon/30 transition-colors cursor-pointer">
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-futuristic-neon/20 flex items-center justify-center group-hover:bg-futuristic-neon/30 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-futuristic-neon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </div>
                      <h4 className="text-lg font-medium text-white group-hover:text-futuristic-neon transition-colors">API Integration Guide</h4>
                    </div>
                    <p className="text-sm text-gray-400">Integrate Sketchplay with your applications</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 relative">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-futuristic-neon/50 to-transparent"></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
            className="bg-futuristic-dark/40 backdrop-blur-md rounded-2xl border border-white/10 p-8 md:p-10 shadow-xl"
          >
            <div className="relative">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-futuristic-blue via-futuristic-purple to-futuristic-neon opacity-30 blur-sm rounded-lg"></div>
              <div className="relative aspect-w-16 aspect-h-7 rounded-lg overflow-hidden bg-futuristic-black/40">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d220852.95123615643!2d-81.87905416885496!3d30.345317934407498!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88e5b716f1ceafeb%3A0xc4cd7d3896fcc7e2!2sJacksonville%2C%20FL!5e0!3m2!1sen!2sus!4v1710284774345!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0, filter: 'grayscale(1) contrast(1.2) brightness(0.8)' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="rounded-lg"
                  title="Sketchplay office location in Jacksonville, FL"
                ></iframe>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
} 